import Phaser from 'phaser';
import { SceneKeys } from './SceneKeys';
import { loadMetaState, saveMetaState } from '../core/save';
import { createRunState } from '../core/state';
import { Rng } from '../core/rng';
import type { MetaState } from '../core/types';
import { applyMetaProgressionToRunState, findChapterIndex, getCurrentChapter } from '../meta';
import { LobbyHeader, HEADER_HEIGHT } from '../ui/lobby/LobbyHeader';
import { BottomNav, NAV_HEIGHT, type NavTabDef } from '../ui/lobby/BottomNav';
import { PlayPanel } from '../ui/lobby/PlayPanel';
import { UpgradesPanel } from '../ui/lobby/UpgradesPanel';
import { GearPanel } from '../ui/lobby/GearPanel';
import { HEX } from '../ui/lobby/palette';

const TABS: NavTabDef[] = [
  { id: 'pelear', label: 'Pelear' },
  { id: 'mejoras', label: 'Mejoras' },
  { id: 'equipo', label: 'Equipo' },
  { id: 'mision', label: 'Misiones', locked: true },
  { id: 'liga', label: 'Liga', locked: true },
];

/**
 * Lobby: isla flotante con el capítulo actual + botón PELEAR, nav inferior
 * de 5 pestañas (Mejoras y Equipo funcionales, 2 "Próximamente" con
 * candado) y header con avatar/nivel/monedas/gemas. Carga y guarda el
 * `MetaState` con `core/save` (autosave tras cada compra/merge/equip).
 */
export class LobbyScene extends Phaser.Scene {
  private meta!: MetaState;
  private rng!: Rng;
  private header!: LobbyHeader;
  private readonly panels = new Map<string, Phaser.GameObjects.Container>();
  private toast?: Phaser.GameObjects.Text;

  constructor() {
    super(SceneKeys.Lobby);
  }

  create(): void {
    this.meta = loadMetaState();
    // semilla por sesión: alcanza para fusiones/drops del lobby, no necesita
    // ser reproducible entre partidas (a diferencia del rng de una run).
    this.rng = new Rng((Date.now() ^ 0x9e3779b9) >>> 0);

    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor(HEX.bg);

    const contentY = HEADER_HEIGHT;
    const contentHeight = height - HEADER_HEIGHT - NAV_HEIGHT;

    const chapter = getCurrentChapter(this.meta);

    const playPanel = new PlayPanel(this, width, contentHeight, chapter, () => this.startRun());
    playPanel.setPosition(0, contentY);
    this.panels.set('pelear', playPanel);

    const upgradesPanel = new UpgradesPanel(this, width, contentHeight, this.meta, (next) =>
      this.commitMeta(next)
    );
    upgradesPanel.setPosition(0, contentY);
    this.panels.set('mejoras', upgradesPanel);

    const gearPanel = new GearPanel(this, 0, contentY, width, contentHeight, this.meta, this.rng, (next) =>
      this.commitMeta(next)
    );
    this.panels.set('equipo', gearPanel);

    // header y nav se agregan al final para quedar por encima de los paneles.
    this.header = new LobbyHeader(this, width, this.meta);

    new BottomNav(
      this,
      width,
      height - NAV_HEIGHT,
      TABS,
      'pelear',
      (id) => this.showTab(id),
      (def) => this.showToast(`${def.label}: próximamente`)
    );

    this.showTab('pelear');

    // Puente e2e/depuración: permite disparar PELEAR sin depender de las
    // coordenadas del botón dentro del canvas. Se limpia al salir del lobby.
    (window as unknown as { __vyvLobby?: unknown }).__vyvLobby = {
      pelear: () => this.startRun(),
    };
    this.events.once('shutdown', () => {
      delete (window as unknown as { __vyvLobby?: unknown }).__vyvLobby;
    });
  }

  private showTab(id: string): void {
    for (const [key, panel] of this.panels) {
      panel.setVisible(key === id);
    }
  }

  /** Aplica un MetaState nuevo (tras comprar/equipar/fusionar): guarda y refresca toda la UI. */
  private commitMeta(next: MetaState): void {
    this.meta = next;
    saveMetaState(this.meta);
    this.header.refresh(this.meta);
    (this.panels.get('mejoras') as UpgradesPanel).refresh(this.meta);
    (this.panels.get('equipo') as GearPanel).refresh(this.meta);
  }

  /** Arma el RunState inicial (mejoras + gear aplicados) y navega a la escena Game. */
  private startRun(): void {
    const chapter = getCurrentChapter(this.meta);
    const chapterIndex = findChapterIndex(chapter.id);
    const baseRun = createRunState({ chapterId: chapter.id, totalWaves: chapter.waves.length });
    const runState = applyMetaProgressionToRunState(baseRun, this.meta);
    this.scene.start(SceneKeys.Game, { runState, chapterId: chapter.id, chapterIndex });
  }

  private showToast(message: string): void {
    this.toast?.destroy();
    const { width, height } = this.scale;
    this.toast = this.add
      .text(width / 2, height - NAV_HEIGHT - 24, message, {
        fontFamily: 'monospace',
        fontSize: '13px',
        color: '#f4f0ff',
        backgroundColor: '#241c3d',
        padding: { x: 12, y: 6 },
      })
      .setOrigin(0.5)
      .setDepth(50);
    this.time.delayedCall(1600, () => this.toast?.destroy());
  }
}
